import { supabase } from './supabase'
import type { BillingMilestone, MilestoneStatus } from '../types/agreement'
import {
  computeMilestoneAmounts,
  copyMilestonesForTotal,
  roundMoney,
  sumMilestonePercentages,
  validateMilestoneSchedule,
} from './milestoneBillingMath'

export {
  computeMilestoneAmounts,
  copyMilestonesForTotal,
  roundMoney,
  sumMilestonePercentages,
  validateMilestoneSchedule,
}

export function createEmptyMilestone(sortOrder = 0): BillingMilestone {
  return {
    id: `new-${Date.now()}-${sortOrder}`,
    label: '',
    percentage: 0,
    amount: 0,
    sort_order: sortOrder,
    status: 'pending',
  }
}

/** PostgREST when the table is not in the schema cache (migration not applied). */
function isMilestonesTableMissing(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false
  if (error.code === 'PGRST205') return true
  const msg = error.message || ''
  return msg.includes('agreement_billing_milestones') && msg.includes('schema cache')
}

function mapRow(row: any): BillingMilestone {
  return {
    id: row.id,
    proposal_id: row.proposal_id,
    contract_id: row.contract_id,
    label: row.label || '',
    percentage: Number(row.percentage || 0),
    amount: Number(row.amount || 0),
    sort_order: Number(row.sort_order || 0),
    status: (row.status as MilestoneStatus) || 'pending',
    invoice_id: row.invoice_id,
    invoiced_at: row.invoiced_at,
    notes: row.notes,
  }
}

export async function fetchProposalMilestones(proposalId: string): Promise<BillingMilestone[]> {
  const { data, error } = await supabase
    .from('agreement_billing_milestones')
    .select('*')
    .eq('proposal_id', proposalId)
    .order('sort_order', { ascending: true })

  if (error) {
    if (isMilestonesTableMissing(error)) {
      console.warn(
        'agreement_billing_milestones table not found — run migration 20260625_progress_billing_milestones.sql'
      )
      return []
    }
    throw error
  }
  return (data || []).map(mapRow)
}

export async function fetchContractMilestones(contractId: string): Promise<BillingMilestone[]> {
  const { data, error } = await supabase
    .from('agreement_billing_milestones')
    .select('*')
    .eq('contract_id', contractId)
    .order('sort_order', { ascending: true })

  if (error) {
    if (isMilestonesTableMissing(error)) {
      console.warn(
        'agreement_billing_milestones table not found — run migration 20260625_progress_billing_milestones.sql'
      )
      return []
    }
    throw error
  }
  return (data || []).map(mapRow)
}

export async function saveProposalMilestones(
  proposalId: string,
  milestones: BillingMilestone[],
  total: number
): Promise<void> {
  const computed = computeMilestoneAmounts(total, milestones)
  await supabase.from('agreement_billing_milestones').delete().eq('proposal_id', proposalId)

  if (computed.length === 0) return

  const rows = computed.map((m, index) => ({
    proposal_id: proposalId,
    contract_id: null,
    label: m.label.trim(),
    percentage: m.percentage,
    amount: m.amount,
    sort_order: index,
    status: m.status || 'pending',
    notes: m.notes || null,
  }))

  const { error } = await supabase.from('agreement_billing_milestones').insert(rows)
  if (error) throw error
}

export async function saveContractMilestones(
  contractId: string,
  milestones: BillingMilestone[],
  total: number
): Promise<void> {
  const computed = computeMilestoneAmounts(total, milestones)
  const existing = await fetchContractMilestones(contractId)
  const locked = existing.filter((m) => m.status === 'invoiced' || m.status === 'paid')

  await supabase
    .from('agreement_billing_milestones')
    .delete()
    .eq('contract_id', contractId)
    .eq('status', 'pending')

  const pendingRows = computed
    .filter((m) => m.status === 'pending' || !locked.some((l) => l.id === m.id))
    .filter((m) => !locked.some((l) => l.id === m.id))
    .map((m, index) => ({
      proposal_id: null,
      contract_id: contractId,
      label: m.label.trim(),
      percentage: m.percentage,
      amount: m.amount,
      sort_order: locked.length + index,
      status: 'pending',
      notes: m.notes || null,
    }))

  if (pendingRows.length > 0) {
    const { error } = await supabase.from('agreement_billing_milestones').insert(pendingRows)
    if (error) throw error
  }

  for (const lockedMilestone of locked) {
    const match = computed.find((m) => m.id === lockedMilestone.id)
    const amount = match
      ? match.amount
      : roundMoney((total * lockedMilestone.percentage) / 100)
    await supabase
      .from('agreement_billing_milestones')
      .update({ amount, sort_order: lockedMilestone.sort_order })
      .eq('id', lockedMilestone.id)
  }
}

export async function copyProposalMilestonesToContract(
  proposalId: string,
  contractId: string,
  contractTotal: number
): Promise<void> {
  const proposalMilestones = await fetchProposalMilestones(proposalId)
  if (proposalMilestones.length === 0) return
  const copied = copyMilestonesForTotal(proposalMilestones, contractTotal)
  await saveContractMilestones(contractId, copied, contractTotal)
}

export function summarizeMilestones(milestones: BillingMilestone[], contractTotal?: number) {
  const billed = milestones
    .filter((m) => m.status === 'invoiced' || m.status === 'paid')
    .reduce((sum, m) => sum + Number(m.amount || 0), 0)
  const total =
    contractTotal ?? milestones.reduce((sum, m) => sum + Number(m.amount || 0), 0)
  const remaining = Math.max(0, roundMoney(total - billed))
  const nextPending = milestones.find((m) => m.status === 'pending')
  return { billed, total, remaining, nextPending }
}
